import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  Building2,
  Swords,
  Trophy,
  Wallet,
  AlertTriangle,
  Clock,
  FileSearch,
  PenLine,
  ShieldAlert,
  ClipboardCheck,
  Calculator
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Badge, type BadgeVariant } from '@/components/ui/Badge';
import { useDashboardConsultorResumo } from '@/hooks/useDashboard';
import { useLicitacoesFeed } from '@/hooks/useLicitacoes';
import type { PipelineEtapa } from '@/types/gestaoLicitacoes';

const ETAPA_LABELS: Record<PipelineEtapa, string> = {
  captacao: 'Captação',
  em_analise: 'Em análise',
  em_disputa: 'Em disputa',
  ganha: 'Ganha',
  perdida: 'Perdida'
};

const ETAPA_BADGE_VARIANT: Record<PipelineEtapa, BadgeVariant> = {
  captacao: 'default',
  em_analise: 'info',
  em_disputa: 'warning',
  ganha: 'success',
  perdida: 'danger'
};

const AGENTES_IA = [
  { to: '/agentes-ia/oraculo-edital', label: 'Oráculo do Edital', icon: FileSearch },
  { to: '/agentes-ia/redator-ninja', label: 'Redator Ninja', icon: PenLine },
  { to: '/agentes-ia/guardiao-disputa', label: 'Guardião da Disputa', icon: ShieldAlert },
  { to: '/agentes-ia/auditor-360', label: 'Auditor 360°', icon: ClipboardCheck },
  { to: '/agentes-ia/central-planilhas', label: 'Central de Planilhas', icon: Calculator }
];

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(value);

const formatDeadline = (dateString: string | null) => {
  if (!dateString) return 'Sem prazo';
  const dateOnly = dateString.split('T')[0];
  const [year, month, day] = dateOnly.split('-').map((value) => parseInt(value, 10));

  if (!year || !month || !day) return 'Sem prazo';

  const deadline = new Date(year, month - 1, day);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.ceil((deadline.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Encerrado';
  if (diffDays === 0) return 'Hoje';
  if (diffDays === 1) return '1 dia';
  return `${diffDays} dias`;
};

interface IndicadorCardProps {
  title: string;
  value: string | number;
  icon: React.ElementType;
}

function IndicadorCard({ title, value, icon: Icon }: IndicadorCardProps) {
  return (
    <Card variant="hover">
      <CardHeader className="flex flex-row items-center justify-between pb-2">
        <CardTitle className="text-sm font-medium text-ds-text-secondary">{title}</CardTitle>
        <div className="w-10 h-10 rounded-lg bg-ds-surface2 flex items-center justify-center">
          <Icon className="h-5 w-5 text-ds-success" />
        </div>
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold text-ds-text-primary">{value}</div>
      </CardContent>
    </Card>
  );
}

function Dashboard() {
  const { data: resumo, isLoading: resumoLoading, isError: resumoError } = useDashboardConsultorResumo();
  const { data: licitacoesData, isLoading: licitacoesLoading, isError: licitacoesError } = useLicitacoesFeed({ page: 1, limit: 40 });

  const indicadores = resumo?.indicadores;
  const prazosProximos = resumo?.prazosProximos ?? [];
  const carteiraEmpresas = resumo?.carteiraEmpresas ?? [];
  const recentLicitacoes = licitacoesData?.licitacoes || [];

  const featuredLicitacoes = useMemo(() => {
    const abertas = recentLicitacoes.filter((lic) => formatDeadline(lic.prazo_abertura) !== 'Encerrado');

    const sortedAbertas = [...abertas].sort((a, b) => {
      const timeA = a.prazo_abertura ? new Date(a.prazo_abertura.split('T')[0]).getTime() : Number.MAX_SAFE_INTEGER;
      const timeB = b.prazo_abertura ? new Date(b.prazo_abertura.split('T')[0]).getTime() : Number.MAX_SAFE_INTEGER;
      return timeA - timeB;
    });

    if (sortedAbertas.length > 0) {
      return sortedAbertas.slice(0, 3);
    }

    return recentLicitacoes.slice(0, 3);
  }, [recentLicitacoes]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-ds-text-primary">Painel do Consultor</h1>
        <p className="text-ds-text-secondary mt-1">
          Sua carteira de clientes e o que precisa de atenção agora.
        </p>
      </div>

      {resumoError && (
        <Card>
          <CardContent className="py-4">
            <p className="text-sm text-ds-danger">Não foi possível carregar o resumo da carteira.</p>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <IndicadorCard title="Empresas em carteira" value={indicadores?.empresasEmCarteira ?? 0} icon={Building2} />
        <IndicadorCard title="Licitações em disputa" value={indicadores?.licitacoesEmDisputa ?? 0} icon={Swords} />
        <IndicadorCard
          title="Valor em disputa"
          value={formatCurrency(indicadores?.valorEmDisputa ?? 0)}
          icon={Wallet}
        />
        <IndicadorCard title="Licitações ganhas" value={indicadores?.licitacoesGanhas ?? 0} icon={Trophy} />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-ds-warning" />
            Atenção necessária — prazos nos próximos 7 dias
          </CardTitle>
        </CardHeader>
        <CardContent>
          {resumoLoading ? (
            <p className="text-sm text-ds-text-secondary">Carregando prazos...</p>
          ) : prazosProximos.length === 0 ? (
            <p className="text-sm text-ds-text-secondary">Nenhuma licitação em gestão com prazo crítico nos próximos 7 dias.</p>
          ) : (
            <div className="space-y-3">
              {prazosProximos.map((prazo) => (
                <Link
                  key={`${prazo.empresaId}-${prazo.licitacaoId}`}
                  to={`/gestao-licitacoes?empresa=${prazo.empresaId}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-ds-surface2 border border-ds-border hover:border-ds-success/50 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-ds-text-primary truncate">{prazo.objeto}</p>
                    <p className="text-xs text-ds-text-secondary mt-0.5">
                      {prazo.empresaNome}
                      {prazo.orgao ? ` · ${prazo.orgao}` : ''}
                      {prazo.uf ? ` · ${prazo.uf}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Badge variant={ETAPA_BADGE_VARIANT[prazo.etapa]} size="sm">
                      {ETAPA_LABELS[prazo.etapa]}
                    </Badge>
                    <span className="text-xs font-semibold text-ds-warning flex items-center gap-1">
                      <Clock className="h-3.5 w-3.5" />
                      {formatDeadline(prazo.prazoAbertura)}
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Carteira de clientes</CardTitle>
        </CardHeader>
        <CardContent>
          {resumoLoading ? (
            <p className="text-sm text-ds-text-secondary">Carregando carteira...</p>
          ) : carteiraEmpresas.length === 0 ? (
            <p className="text-sm text-ds-text-secondary">Nenhuma empresa cadastrada ainda.</p>
          ) : (
            <div className="space-y-2">
              {carteiraEmpresas.map((empresa) => (
                <Link
                  key={empresa.empresaId}
                  to={`/gestao-licitacoes?empresa=${empresa.empresaId}`}
                  className="flex items-center justify-between gap-3 p-3 rounded-lg bg-ds-surface2 border border-ds-border hover:border-ds-success/50 transition-colors"
                >
                  <p className="text-sm font-medium text-ds-text-primary">{empresa.empresaNome}</p>
                  <div className="flex items-center gap-2 flex-wrap justify-end">
                    {empresa.total === 0 ? (
                      <span className="text-xs text-ds-text-secondary">Sem licitações em gestão</span>
                    ) : (
                      (Object.keys(ETAPA_LABELS) as PipelineEtapa[])
                        .filter((etapa) => empresa.porEtapa[etapa] > 0)
                        .map((etapa) => (
                          <Badge key={etapa} variant={ETAPA_BADGE_VARIANT[etapa]} size="sm">
                            {ETAPA_LABELS[etapa]}: {empresa.porEtapa[etapa]}
                          </Badge>
                        ))
                    )}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Agentes de IA</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {AGENTES_IA.map(({ to, label, icon: Icon }) => (
              <Link
                key={to}
                to={to}
                className="flex flex-col items-center gap-2 p-4 rounded-lg bg-ds-surface2 border border-ds-border hover:border-ds-success/50 transition-colors text-center"
              >
                <Icon className="h-6 w-6 text-ds-success" />
                <span className="text-xs font-medium text-ds-text-primary">{label}</span>
              </Link>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Licitações em Destaque</CardTitle>
        </CardHeader>
        <CardContent>
          {licitacoesLoading ? (
            <p className="text-sm text-ds-text-secondary">Carregando licitações...</p>
          ) : licitacoesError ? (
            <p className="text-sm text-ds-danger">Falha ao carregar licitações em destaque.</p>
          ) : featuredLicitacoes.length === 0 ? (
            <p className="text-sm text-ds-text-secondary">Nenhuma licitação encontrada.</p>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {featuredLicitacoes.map((lic) => {
                const deadlineLabel = formatDeadline(lic.prazo_abertura);
                const isClosed = deadlineLabel === 'Encerrado';

                return (
                  <div key={lic.id} className="p-4 rounded-lg bg-ds-surface2 border border-ds-border">
                    <div className="flex items-start justify-between mb-2 gap-3">
                      <h4 className="font-medium text-ds-text-primary">{lic.objeto}</h4>
                      <Badge variant={isClosed ? 'default' : 'success'}>
                        {isClosed ? 'Encerrada' : 'Aberta'}
                      </Badge>
                    </div>
                    <p className="text-sm text-ds-text-secondary mb-2">{lic.orgao || 'Órgão não informado'}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-semibold text-ds-success">{formatCurrency(Number(lic.valor_estimado || 0))}</span>
                      <span className="text-sm text-ds-text-secondary flex items-center gap-1">
                        <Clock className="h-4 w-4" />
                        {deadlineLabel}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export { Dashboard };
